import { useTheme } from '../../hooks/useTheme'
import { useTranslation } from '../../i18n'
import { useAuth } from '../../contexts/AuthContext'
import type { TrainingSet, TrainingSetStatus } from '../../types/api'
import { translateTrainingSetStatus } from '../../utils/statusTranslations'
import { formatDateRange, getDayAbbreviation } from '../../utils/dateTranslations'
import { StatusBadge } from './StatusBadge'

interface TrainingSetCardProps {
  trainingSet: TrainingSet
  userType: 'trainee' | 'coach'
  userId: string
  onClick: () => void
  /** Visual treatment. Defaults from status when omitted. */
  variant?: 'current' | 'history'
  canManage?: boolean
  isDeleting?: boolean
  onDelete?: () => void
}

function statusVariant(status: TrainingSetStatus): 'success' | 'warning' | 'error' | 'info' | 'default' {
  switch (status) {
    case 'ACTIVE':
      return 'success'
    case 'DRAFT':
      return 'warning'
    case 'CANCELLED':
      return 'error'
    case 'COMPLETED':
      return 'info'
    default:
      return 'default'
  }
}

function isPaused(set: TrainingSet): boolean {
  if (!set.pauseStartDate || !set.pauseEndDate) return false
  const today = new Date()
  today.setHours(12, 0, 0, 0)
  const start = new Date(`${set.pauseStartDate}T12:00:00`)
  const end = new Date(`${set.pauseEndDate}T12:00:00`)
  return today >= start && today <= end
}

/**
 * Training set card for trainee/coach detail screens.
 * Blue accent chrome aligned with Quick Actions / training block list cards.
 */
export function TrainingSetCard({
  trainingSet,
  onClick,
  variant,
  canManage = false,
  isDeleting = false,
  onDelete,
}: TrainingSetCardProps) {
  const theme = useTheme()
  const { t } = useTranslation()
  const { user } = useAuth()
  const isParticipant = user?.id != null
    && (user.id === trainingSet.coachId || user.id === trainingSet.traineeId)

  const isHistory = variant === 'history' || trainingSet.status === 'COMPLETED'
  const showUnpaid = trainingSet.unpaid === true && isParticipant
  const paused = !isHistory && isPaused(trainingSet)
  const dateRange = formatDateRange(
    new Date(trainingSet.startDate),
    new Date(trainingSet.calculatedEndDate),
    t,
  )
  const accent = trainingSet.status === 'DRAFT'
    ? 'rgb(234, 179, 8)'
    : trainingSet.status === 'CANCELLED'
      ? 'rgb(239, 68, 68)'
      : theme.buttonColor
  const showStatusBadges = trainingSet.status !== 'ACTIVE' || showUnpaid || paused
  const showFooter = canManage && !!onDelete

  if (isHistory) {
    const scheduleLabel = trainingSet.days.length > 0
      ? trainingSet.days
          .map(day => getDayAbbreviation(day.day.toUpperCase(), t))
          .join(' · ')
      : null

    return (
      <button
        type="button"
        onClick={onClick}
        className="w-full text-left rounded-2xl px-4 py-4 cursor-pointer transition-opacity active:opacity-70"
        style={{
          backgroundColor: theme.cardBg,
          border: showUnpaid
            ? '1px solid rgba(234, 179, 8, 0.45)'
            : `1px solid ${theme.cardBorder}`,
        }}
      >
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate" style={{ color: theme.text.primary }}>
              {dateRange}
            </p>
            <p className="text-xs mt-0.5" style={{ color: theme.text.muted }}>
              {trainingSet.plannedTrainings} {t.trainingSetStats.planned.toLowerCase()}
              {scheduleLabel ? ` · ${scheduleLabel}` : ''}
            </p>
          </div>
          {showUnpaid && (
            <StatusBadge
              status={t.allTrainingSets.unpaid}
              variant="warning"
              size="sm"
            />
          )}
          <span className="text-xl opacity-50 shrink-0" aria-hidden>→</span>
        </div>
      </button>
    )
  }

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{
        backgroundColor: theme.cardBg,
        border: showUnpaid
          ? '1px solid rgba(234, 179, 8, 0.5)'
          : `1px solid ${theme.cardBorder}`,
      }}
    >
      <div className="flex">
        <div
          className="w-1 shrink-0 self-stretch"
          style={{ backgroundColor: accent }}
          aria-hidden
        />

        <div className="flex-1 min-w-0">
          <button
            type="button"
            onClick={onClick}
            className="w-full text-left px-4 py-4 transition-opacity active:opacity-70"
          >
            {showStatusBadges && (
              <div className="flex items-center gap-2 flex-wrap mb-3">
                {trainingSet.status !== 'ACTIVE' && (
                  <StatusBadge
                    status={translateTrainingSetStatus(trainingSet.status, t)}
                    variant={statusVariant(trainingSet.status)}
                    size="sm"
                  />
                )}
                {showUnpaid && (
                  <StatusBadge status={t.allTrainingSets.unpaid} variant="warning" size="sm" />
                )}
                {paused && (
                  <StatusBadge
                    status={t.userDetail.trainingSets.paused}
                    variant="warning"
                    size="sm"
                  />
                )}
              </div>
            )}

            <p
              className="text-lg font-bold leading-snug mb-3"
              style={{ color: theme.text.primary }}
            >
              {dateRange}
            </p>

            <div
              className="flex items-stretch gap-0 rounded-xl overflow-hidden mb-3"
              style={{
                backgroundColor: theme.isDark ? 'rgba(0,136,204,0.1)' : 'rgba(0,136,204,0.06)',
              }}
            >
              <div className="flex-1 px-3.5 py-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide mb-1" style={{ color: theme.text.muted }}>
                  {t.userDetail.trainingSets.sessionsLabel}
                </div>
                <div className="text-2xl font-bold tabular-nums leading-none" style={{ color: theme.text.primary }}>
                  {trainingSet.plannedTrainings}
                </div>
              </div>
              <div
                className="w-px self-stretch my-2.5"
                style={{ backgroundColor: theme.cardBorder }}
                aria-hidden
              />
              <div className="flex-1 px-3.5 py-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide mb-1" style={{ color: theme.text.muted }}>
                  {t.userDetail.trainingSets.scheduleLabel}
                </div>
                <div className="text-sm font-semibold leading-tight" style={{ color: theme.text.primary }}>
                  {trainingSet.days.length > 0
                    ? trainingSet.days
                        .map(day => getDayAbbreviation(day.day.toUpperCase(), t))
                        .join(' · ')
                    : '—'}
                </div>
              </div>
            </div>

            {trainingSet.days.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {trainingSet.days.map((day, idx) => (
                  <span
                    key={`${day.day}-${idx}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: `${theme.buttonColor}12`,
                      border: `1px solid ${theme.buttonColor}25`,
                      color: theme.accent.text,
                    }}
                  >
                    <span>{getDayAbbreviation(day.day.toUpperCase(), t)}</span>
                    {day.startTime && (
                      <span style={{ opacity: 0.75 }}>{day.startTime.slice(0, 5)}</span>
                    )}
                  </span>
                ))}
              </div>
            )}
          </button>

          {showFooter && (
            <div
              className="flex items-center justify-between px-3 py-2"
              style={{ borderTop: `1px solid ${theme.cardBorder}` }}
            >
              <div />
              <button
                type="button"
                title={t.userDetail.trainingSets.deleteAria}
                onClick={onDelete}
                disabled={isDeleting}
                className="p-3 rounded-xl transition-colors disabled:opacity-40"
                style={{ color: '#ef4444' }}
                aria-label={t.userDetail.trainingSets.deleteAria}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                  <path d="M10 11v6"/><path d="M14 11v6"/>
                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
